/**
 * Wire contract between the login components (browser) and the login route handlers (server).
 * Both sides import it, so a request or response shape is declared once.
 *
 * Every route answers `POST` with a JSON body. The apps mount the handlers under one base path,
 * `/api/auth` in both apps, e.g. `/api/auth/password`.
 */

import { isRecord } from './is.record'

/** Route names below the app's login base path. */
export const LOGIN_ROUTES = {
  /** `requestEmailToken`: decides how the user signs in (password, email link, Zitadel). */
  challenge: 'challenge',
  /** `loginUpdatePassword`: sets a new password with the token of an expired one. */
  changePassword: 'change-password',
  /** `exchangeEmailToken`: signs in with the token of an emailed login link. */
  email: 'email',
  /** `exchangeGoogleToken`: signs in with a Google authorization code. */
  google: 'google',
  /** `loginWithEmailAndPassword`. */
  password: 'password',
} as const

/** Error of the Tucan API, passed on as the API sent it. `message` is the error code. */
export interface ApiError {
  message: string
  extensions?: Record<string, unknown>
  [key: string]: unknown
}

/**
 * Reads a GraphQL `errors` list, keeping the entries that carry a string `message` as they are.
 * Anything else yields `[]`.
 */
export function readApiErrors(value: unknown): ApiError[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry: unknown): ApiError[] =>
    isRecord(entry) && typeof entry.message === 'string'
      ? [{ ...entry, message: entry.message }]
      : []
  )
}

/** Error codes of the Tucan API the login flow reacts to. */
export const API_ERROR = {
  /** Unknown user, or wrong password. */
  email: 'ERROR_EMAIL',
  /** The redirect of an email link is not allowed. */
  redirect: 'ERROR_REDIRECT',
  /** The password expired; `extensions.originalError.error` holds the change-password token. */
  passwordExpired: 'PASSWORD_EXPIRED',
  /** The new password does not meet the password policy. */
  passwordNotGoodEnough: 'PASSWORD_NOT_GOOD_ENOUGH',
} as const

/** How `requestEmailToken` wants the user to sign in. */
export const CHALLENGE = {
  /** A login link was emailed. */
  email: 'CHALLENGE_EMAIL',
  password: 'CHALLENGE_PASSWORD',
  zitadel: 'CHALLENGE_ZITADEL',
} as const

/** Body of `password`. */
export interface PasswordLoginRequest {
  email: string
  password: string
}

/** Body of `email`. */
export interface EmailLoginRequest {
  /** Token of the login link. */
  token: string
}

/** Body of `google`. */
export interface GoogleLoginRequest {
  /** Authorization code of Google's `auth-code` flow (redirect URI `postmessage`). */
  code: string
}

/** Body of `challenge`. */
export interface ChallengeRequest {
  email: string
  /** URL the emailed login link points to; the token is appended. The API validates it. */
  redirect: string
  /** Ask for the password instead of emailing a link, when the account has one. */
  verifyWithPassword: boolean
}

/** Body of `change-password`. */
export interface ChangePasswordRequest {
  /** Token from the `PASSWORD_EXPIRED` error. */
  token: string
  password: string
}

/** `200` of the sign-in routes (`password`, `email`, `google`); the session cookie is set. */
export interface LoginSuccessResponse {
  login: true
}

/** `200` of `challenge` and `change-password`: the API's status message, e.g. a `CHALLENGE`. */
export interface StatusResponse {
  status: string
}

/** Failure the API refused: `401` for the sign-in routes, `400` for the others. */
export interface ApiErrorsResponse {
  errors: ApiError[]
}

/**
 * `500` of `password` when the API answered without tokens and without errors. The shape is the
 * one the ui's route always had.
 */
export interface NoTokenResponse {
  errors: ['no token received']
}

/** `400` when the request body misses a field; `404` for Google when it is not configured. */
export interface RequestErrorResponse {
  error: string
}

/** HTTP header that tells the API which language its emails use. */
export const LANGUAGE_HEADER = 'x-language'
